import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import toast from 'react-hot-toast';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsDoNotMatch'));
      return;
    }
    if (password.length < 8) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      toast.success(t('auth.passwordResetSuccess'));
    } catch {
      toast.error(t('auth.resetTokenInvalid'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="text-center">
        <p className="text-red-600 mb-4">{t('auth.resetTokenMissing')}</p>
        <Link to="/forgot-password" className="text-primary-600 hover:text-primary-500 font-medium text-sm">
          {t('auth.requestNewLink')}
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <div className="lg:hidden flex items-center gap-2 mb-6">
          <img src="/logo.svg" alt="Ledgora" className="w-10 h-10" />
          <span className="text-xl font-bold text-gray-900">{import.meta.env.VITE_APP_NAME}</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.resetPassword')}</h2>
        <p className="mt-2 text-sm text-gray-600">{t('auth.resetPasswordDescription')}</p>
      </div>

      {done ? (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-800">
          <p className="font-medium mb-1">{t('auth.passwordResetSuccess')}</p>
          <Link to="/login" className="mt-4 inline-block text-primary-600 hover:text-primary-500 font-medium text-sm">
            ← {t('auth.backToLogin')}
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="password" className="label">{t('auth.newPassword')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
              autoFocus
              minLength={8}
              placeholder="••••••••"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="label">{t('auth.confirmPassword')}</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              required
              minLength={8}
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? t('common.loading') : t('auth.resetPassword')}
          </button>

          <div className="text-center">
            <Link to="/login" className="text-sm text-primary-600 hover:text-primary-500 font-medium">
              ← {t('auth.backToLogin')}
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}
